import type { ArticleInterface } from '~/types/AppData/PageContent/ArticleInterface';
import type { PropertySettingsInterface } from '~/types/AppData/PropertySettingsInterface';
import type { PayloadInterface } from '~/types/AppData/AppDataInterface';
import { getCookieByName } from '~/utilities/data/getCookieByName';
import { Logger } from '~/utilities/logger';
import { maybeGetSlide } from '~/utilities/maybeGetSlide';
import type { SlideshowInterface } from '~/types/AppData/PageContent/SlideshowInterface';

type DataLayerEvent = {
  event: string;
  eventLabel: string;
  eventAction: string;
  isSlideshow: boolean;
  finiteScrollIndex?: number;
  moreProperties?: {
    slideNumber?: number;
    slideControlsClick?: 'prev' | 'next';
  };
};

/**
 * The GA class is responsible for handling Google Analytics events related to page views.
 * It processes the article and payload data passed during instantiation to create detailed
 * event objects that are pushed to the Google Analytics data layer for tracking purposes.
 */
export class GA {
  private article: ArticleInterface | undefined;
  private property: PropertySettingsInterface;
  private experiments: any;
  private slideshow?: SlideshowInterface;
  private currentSlide?: number;
  private type: string;
  private url: string;
  private urlObject: URL;
  private additionalPosts: any;
  private isMobileViewer: any;
  private other?: any;
  private canonicalURL: string;
  constructor(
    article: ArticleInterface | undefined,
    payload: PayloadInterface,
    other?: any
  ) {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    this.article = article;
    this.slideshow = payload.pageContent.slideshow;
    this.property = payload.propertySettings;
    this.experiments = payload.experiments;
    this.type = payload.type;
    this.additionalPosts = payload.pageContent.additionalPosts;
    this.isMobileViewer = payload.isMobileViewer;
    this.currentSlide =
      other?.currentSlide ?? payload.pageContent.slideshow?.currentSlide;
    this.other = other;
    this.canonicalURL = payload.canonicalURL;
    this.url = article?.articleUrl
      ? article.articleUrl
      : payload.canonicalURL
        ? payload.canonicalURL
        : '';
    this.urlObject = new URL(window.location.href);
  }

  private getArticleURL() {
    // Finite scroll - if an articleUrl exists on the article object, then it should be a finite scroll article
    if (this.other?.isFiniteScroll && this.article?.articleUrl) {
      return this.article.articleUrl;
    }

    // Returns url for slideshow and regular article urls
    return this.other?.currentSlide
      ? `${this.url}/${this.currentSlide}`
      : window.location.href;
  }

  private getURLQueryParam(key: string) {
    return this.urlObject.searchParams.get(key);
  }

  private getURLPathnameValueAfterKey(key: string) {
    const pathnameParts = this.urlObject.pathname.split('/');
    const indexOfKey = pathnameParts.indexOf(key);
    return indexOfKey !== -1 ? pathnameParts[indexOfKey + 1] : null;
  }

  private getAffiliateId() {
    return (
      this.getURLQueryParam('a_aid') ||
      this.getURLPathnameValueAfterKey('partners')
    );
  }

  private getBehaviorLabels() {
    const behaviorLabels = this.article?.metadata?.commercialTags;
    return behaviorLabels === undefined ? null : behaviorLabels;
  }

  private getBlockNames() {
    return maybeGetSlide(
      this.article,
      this.slideshow,
      this.currentSlide ?? 1
    ).map((block) => block.type);
  }

  private getPublishedAt() {
    if (!this.article) return '';

    const parts = this.article.createdAt.split('T');
    const date = parts[0].replace(/-/g, '');
    const time = parts[1].split(':');
    return `${date}${time[0]}${time[1]}`;
  }

  // TODO: Remove this once we have a proper way to handle the page type
  private getPageType() {
    if (this.canonicalURL === 'https://www.si.com/') {
      return 'HomePage';
    }

    return this.type;
  }

  private getPageViewEvent(event: DataLayerEvent) {
    return {
      event: event.event,
      event_label: event.eventLabel,
      GA_event_action: event.eventAction,
      ownerUsername: this.article?.authors.owner?.username,
      articleId: this.article?.id,
      articleTitle: this.article?.title,
      affiliateId: this.getAffiliateId(),
      isSlideshow: event.isSlideshow,
      dfpProperty: this.property.ampDfpProperty,
      property: this.property.property,
      behaviorLabels: this.getBehaviorLabels(),
      articleAuthor: this.article?.authors.owner?.name,
      articleURL: this.getArticleURL(),
      authorsUsernames: this.article?.authors.owner?.username,
      distributionChannels: this.article?.feeds,
      mainMedia: this.article?.cover.media?.type
        ? this.article.cover.media.type
        : this.article?.analysis?.coverMediaType
          ? this.article.analysis.coverMediaType
          : 'image',
      articlePublicationTime: this.getPublishedAt(),
      updatedAt: this.article?.updatedAt,
      contentTags: this.article?.tags,
      experiments: this.experiments,
      language: this.property.language,
      pageType: this.getPageType(),
      vertical: this.article?.customVertical ? this.article.customVertical : '',
      finiteScrollDataExists:
        this.additionalPosts?.postsList?.value?.articlesQueryURLs?.length > 0 ||
        false,
      isArticleInFiniteScrollPage:
        this.additionalPosts?.postsList?.value?.articlesQueryURLs?.length > 0 ||
        false,
      articleWordCount: this.article?.analysis?.numberOfWordsTyped || null,
      numberOfArticleOnPage: (event.finiteScrollIndex ?? 0) + 1,
      articleTemplate: this.getBlockNames(),
      platform: this.isMobileViewer ? 'mobile' : 'desktop',
      mmcookieUserID: getCookieByName('mm-user-id'),
      geoDetected: getCookieByName('countryCode'),
      ...event.moreProperties,
    };
  }

  /**
   * Dispatches a custom event with the specified name and data.
   *
   * @param {string} eventName - The name of the custom event to be dispatched.
   * @param {any} eventData - The data to be included in the event's detail property.
   * @return {void} This method does not return a value.
   */
  private sendCustomEvent(eventName: string, eventData: any) {
    const customEvent = new CustomEvent(eventName, { detail: eventData });
    window.dispatchEvent(customEvent);
  }

  /**
   * Conditionally sends a custom event to the event tracking service based on the specified criteria.
   *
   * @param {DataLayerEvent} event - The event object containing event details used to determine the custom event to send.
   * @return {void} This method does not return a value.
   */
  private maybeSendCustomEvent(event: DataLayerEvent) {
    if (event.eventAction === 'Pageview' && event.finiteScrollIndex) {
      this.sendCustomEvent('finiteScrollEvent', {
        message: 'Next Article On finite Scroll',
        articleId: this.article?.id,
      });
    }

    if (
      event.eventAction === 'Slideshow Pageview' &&
      event.moreProperties?.slideControlsClick
    ) {
      this.sendCustomEvent('slideshowEvent', {
        message:
          event.moreProperties.slideControlsClick === 'prev'
            ? 'Prev button clicked'
            : 'Next button clicked',
        slideNumber: event.moreProperties.slideNumber ?? this.currentSlide,
        articleId: this.article?.id,
      });
    }
  }

  /**
   * The `pushPageViewEvent` method is responsible for pushing a new page view event
   * to the Google Analytics data layer. It constructs a detailed event object
   * using the provided event data and various properties from the class instance,
   * and then pushes this object into the `window.dataLayer` array for tracking purposes.
   *
   * @param {DataLayerEvent} event - The event data to be pushed to the data layer.
   */
  public pushPageViewEvent(event: DataLayerEvent) {
    const Log = new Logger();
    Log.info({
      message: 'pushPageViewEvent',
      event,
      article: {
        title: this.article?.title,
        url: this.url,
      },
      full: this.getPageViewEvent(event),
    });

    this.maybeSendCustomEvent(event);

    window.dataLayer.push(this.getPageViewEvent(event));
  }
}
